import React, { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  User, 
  Plus, 
  Loader2, 
  Trash2, 
  CheckCircle2, 
  AlertCircle,
  Clock,
  Sparkles,
  MoreVertical,
  Edit2,
  Copy
} from 'lucide-react'
import { useCharacterStore } from '../../stores/characterStore'
import { Character } from '../../types'
import CreateCharacterDialog from './CreateCharacterDialog'

interface CharactersListProps {
  projectId: string
  onSelectCharacter?: (character: Character) => void
  selectedCharacterId?: string | null
  compact?: boolean
}

const CharactersList: React.FC<CharactersListProps> = ({
  projectId,
  onSelectCharacter,
  selectedCharacterId,
  compact = false
}) => {
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null)
  
  const { characters, fetchCharacters, deleteCharacter, loading } = useCharacterStore()

  useEffect(() => {
    if (projectId) {
      fetchCharacters(projectId)
    }
  }, [projectId, fetchCharacters])

  const handleDelete = async (characterId: string) => {
    if (confirm('Are you sure you want to delete this character? This cannot be undone.')) {
      await deleteCharacter(characterId)
    }
    setMenuOpenId(null)
  }

  const copyTriggerWord = (triggerWord: string) => {
    navigator.clipboard.writeText(triggerWord)
    setMenuOpenId(null)
  }

  const getStatusIcon = (status: Character['status']) => {
    switch (status) {
      case 'ready':
        return <CheckCircle2 className="w-4 h-4 text-green-400" />
      case 'training':
        return <Loader2 className="w-4 h-4 text-purple-400 animate-spin" />
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-400" />
      case 'failed':
        return <AlertCircle className="w-4 h-4 text-red-400" />
    }
  }

  const getStatusLabel = (status: Character['status']) => {
    switch (status) {
      case 'ready':
        return 'Ready'
      case 'training':
        return 'Training...'
      case 'pending':
        return 'Queued'
      case 'failed':
        return 'Failed'
    }
  }

  if (compact) {
    // Compact view for use in generation dialog
    return (
      <div className="space-y-2">
        {loading && characters.length === 0 ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-5 h-5 animate-spin text-white/50" />
          </div>
        ) : characters.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-white/50 text-sm mb-3">No characters trained yet</p>
            <button
              onClick={() => setShowCreateDialog(true)}
              className="glass-button px-4 py-2 text-sm bg-purple-500/20 hover:bg-purple-500/30"
            >
              <Plus className="w-4 h-4 inline mr-2" />
              Create Character
            </button>
          </div>
        ) : (
          <>
            {characters.map((character) => (
              <button
                key={character.id}
                onClick={() => character.status === 'ready' && onSelectCharacter?.(character)}
                disabled={character.status !== 'ready'}
                className={`w-full p-3 rounded-lg border transition-all text-left ${
                  selectedCharacterId === character.id
                    ? 'border-purple-500 bg-purple-500/20'
                    : character.status === 'ready'
                    ? 'border-white/10 hover:border-white/30 bg-white/5 hover:bg-white/10'
                    : 'border-white/5 bg-white/5 opacity-60 cursor-not-allowed'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-pink-500/20 to-purple-600/20 flex items-center justify-center flex-shrink-0">
                    {character.thumbnailUrl ? (
                      <img 
                        src={character.thumbnailUrl} 
                        alt={character.name}
                        className="w-full h-full object-cover rounded-lg"
                      />
                    ) : (
                      <User className="w-5 h-5 text-purple-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-white font-medium truncate">{character.name}</p>
                      {getStatusIcon(character.status)}
                    </div>
                    <p className="text-white/40 text-xs font-mono">@{character.triggerWord}</p>
                  </div>
                </div>
              </button>
            ))}
            <button
              onClick={() => setShowCreateDialog(true)}
              className="w-full p-3 rounded-lg border border-dashed border-white/20 hover:border-purple-400/50 text-white/50 hover:text-white/80 transition-all flex items-center justify-center space-x-2"
            >
              <Plus className="w-4 h-4" />
              <span className="text-sm">Add Character</span>
            </button>
          </>
        )}

        <CreateCharacterDialog
          open={showCreateDialog}
          onClose={() => setShowCreateDialog(false)}
          projectId={projectId}
        />
      </div>
    )
  }

  // Full view for project settings or dedicated characters page
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Sparkles className="w-5 h-5 text-purple-400" />
          <h3 className="text-lg font-headline text-white">Characters</h3>
          <span className="text-white/40 text-sm">({characters.length})</span>
        </div>
        <button
          onClick={() => setShowCreateDialog(true)}
          className="glass-button px-3 py-1.5 text-sm bg-purple-500/20 hover:bg-purple-500/30 flex items-center space-x-1"
        >
          <Plus className="w-4 h-4" />
          <span>New Character</span>
        </button>
      </div>

      {loading && characters.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-white/50" />
        </div>
      ) : characters.length === 0 ? (
        <div className="glass-card bg-white/5 p-8 text-center rounded-xl">
          <div className="w-16 h-16 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <User className="w-8 h-8 text-purple-400" />
          </div>
          <h4 className="text-white font-medium mb-2">No Characters Yet</h4>
          <p className="text-white/50 text-sm mb-4">
            Train custom AI characters from your own images. Use them to generate consistent character images.
          </p>
          <button
            onClick={() => setShowCreateDialog(true)}
            className="glass-button px-4 py-2 bg-purple-500/20 hover:bg-purple-500/30"
          >
            <Plus className="w-4 h-4 inline mr-2" />
            Create Your First Character
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {characters.map((character) => (
            <motion.div
              key={character.id}
              layout
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className={`glass-card p-4 rounded-xl border ${
                selectedCharacterId === character.id
                  ? 'border-purple-500'
                  : 'border-white/10'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-pink-500/20 to-purple-600/20 flex items-center justify-center flex-shrink-0">
                    {character.thumbnailUrl ? (
                      <img 
                        src={character.thumbnailUrl} 
                        alt={character.name}
                        className="w-full h-full object-cover rounded-lg"
                      />
                    ) : (
                      <User className="w-6 h-6 text-purple-400" />
                    )}
                  </div>
                  <div>
                    <h4 className="text-white font-medium">{character.name}</h4>
                    <div className="flex items-center space-x-2">
                      <code className="text-purple-300 text-xs bg-purple-500/20 px-1.5 py-0.5 rounded">
                        @{character.triggerWord}
                      </code>
                      {character.isStyle && (
                        <span className="text-xs text-white/40 bg-white/10 px-1.5 py-0.5 rounded">Style</span>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="relative">
                  <button
                    onClick={() => setMenuOpenId(menuOpenId === character.id ? null : character.id)}
                    className="p-1.5 hover:bg-white/10 rounded-lg text-white/50 hover:text-white transition-colors"
                  >
                    <MoreVertical className="w-4 h-4" />
                  </button>
                  
                  <AnimatePresence>
                    {menuOpenId === character.id && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        className="absolute right-0 top-8 bg-gray-900 border border-white/20 rounded-lg shadow-xl py-1 min-w-[140px] z-10"
                      >
                        <button
                          onClick={() => copyTriggerWord(character.triggerWord)}
                          className="w-full px-3 py-2 text-left text-sm text-white/80 hover:bg-white/10 flex items-center space-x-2"
                        >
                          <Copy className="w-4 h-4" />
                          <span>Copy Trigger</span>
                        </button>
                        <button
                          onClick={() => handleDelete(character.id)}
                          className="w-full px-3 py-2 text-left text-sm text-red-400 hover:bg-red-500/10 flex items-center space-x-2"
                        >
                          <Trash2 className="w-4 h-4" />
                          <span>Delete</span>
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              {character.description && (
                <p className="text-white/50 text-sm mb-3 line-clamp-2">{character.description}</p>
              )}

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  {getStatusIcon(character.status)}
                  <span className="text-sm text-white/60">{getStatusLabel(character.status)}</span>
                </div>
                
                {character.status === 'ready' && onSelectCharacter && (
                  <button
                    onClick={() => onSelectCharacter(character)}
                    className="text-sm text-purple-400 hover:text-purple-300 transition-colors"
                  >
                    Use for Generation
                  </button>
                )}
                
                {character.status === 'training' && character.trainingProgress !== undefined && (
                  <div className="flex items-center space-x-2">
                    <div className="w-16 bg-white/10 rounded-full h-1.5">
                      <div 
                        className="h-full bg-purple-500 rounded-full transition-all"
                        style={{ width: `${character.trainingProgress}%` }}
                      />
                    </div>
                    <span className="text-xs text-white/40">{character.trainingProgress}%</span>
                  </div>
                )}
                
                {character.status === 'failed' && character.trainingError && (
                  <span className="text-xs text-red-400 truncate max-w-[150px]" title={character.trainingError}>
                    {character.trainingError}
                  </span>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <CreateCharacterDialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        projectId={projectId}
      />
    </div>
  )
}

export default CharactersList

